// NavigationBarCapellanes.js
import React, { useState, useEffect, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import { salirSesion } from '../../redux/slices/sessionSlice';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Menu,
  MenuItem,
  Box,
  Avatar,
  Divider,
  Container,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  ListItemIcon,
  Collapse
} from '@mui/material';
import {
  Home as HomeIcon,
  Login as LoginIcon,
  HowToReg as RegisterIcon,
  Logout as LogoutIcon,
  Person as PersonIcon,
  AdminPanelSettings as AdminIcon,
  Menu as MenuIcon,
  Close,
  Language,
  KeyboardArrowDown,
  KeyboardArrowUp,
  Facebook,
  Instagram,
  WhatsApp,
  Church,
  History,
  Favorite,
  EmojiPeople,
  Phone,
  AccountTree,
  Group,
  ExpandLess,
  ExpandMore,
  Info,
  AppRegistration as InscripcionIcon,
} from '@mui/icons-material';
// Importar el logo
import logo from '../../assets/logo1.png';
// Importar traducciones
import { translations, availableLanguages, defaultLanguage } from '../../data/Translations';
// Paleta de colores de Capellanes
const colors = {
  gold: '#D4AF37',
  lightGold: '#F4E4BC',
  darkGold: '#B8960C',
  black: '#1A1A1A',
  darkGrey: '#2D2D2D',
  white: '#FFFFFF',
  offWhite: '#F8F6F0',
  cream: '#FDF8E8'
};
const NavigationBarCapellanes = ({ 
  onScrollToSection,
  historiaRef,
  aboutUsRef,
  valoresRef,
  funcionesRef,
  chaplainciesRef,
  contactoRef,
  onMenuOpenChange // NUEVO: Callback para notificar cuando el menú está abierto
}) => {
  const { isLoggedIn, username, user } = useSelector((state) => state.session);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  // Estado para el idioma actual
  const [currentLang, setCurrentLang] = useState(() => {
    const savedLang = localStorage.getItem('preferredLanguage');
    if (savedLang && translations[savedLang]) return savedLang;
    const browserLang = navigator.language.split('-')[0];
    if (translations[browserLang]) return browserLang;
    return defaultLanguage;
  });
  // Estados de menús
  const [anchorElUser, setAnchorElUser] = useState(null);
  const [anchorElNosotros, setAnchorElNosotros] = useState(null);
  const [langMenuAnchor, setLangMenuAnchor] = useState(null);
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);
  const [mobileNosotrosOpen, setMobileNosotrosOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  // Páginas que son fullscreen (landing pages)
  const fullscreenRoutes = ['/', '/home'];
  const isFullscreenPage = fullscreenRoutes.includes(location.pathname);
  const esAdmin = user?.admin || user?.rol === 'Admin' || (user?.roles && user.roles.includes('Admin'));
  // Obtener traducciones actuales
  const t = translations[currentLang] || translations[defaultLanguage];
  const currentLanguage = availableLanguages.find(lang => lang.code === currentLang);
  const nombreAMostrar = user?.nombreCompleto || 
                        (user?.nombre && user?.apellido ? `${user.nombre} ${user.apellido}` : '') ||
                        username || 
                        'Usuario';
  // NUEVO: Notificar cuando el menú "Nosotros" está abierto
  useEffect(() => {
    if (onMenuOpenChange) {
      onMenuOpenChange(Boolean(anchorElNosotros));
    }
  }, [anchorElNosotros, onMenuOpenChange]);
  // Efecto para manejar el scroll
  useEffect(() => {
    const handleScroll = () => {
      const isScrolled = window.scrollY > 100;
      if (isScrolled !== scrolled) {
        setScrolled(isScrolled);
      }
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [scrolled]);
  // Reset scroll al cambiar de página
  useEffect(() => {
    window.scrollTo(0, 0);
    setScrolled(!isFullscreenPage);
  }, [location.pathname, isFullscreenPage]);
  // Handlers de menús - con useCallback para evitar re-renders innecesarios
  const handleOpenUserMenu = useCallback((event) => {
    setAnchorElUser(event.currentTarget);
  }, []);
  const handleOpenNosotrosMenu = useCallback((event) => {
    setAnchorElNosotros(event.currentTarget);
  }, []);
  const handleLangMenuOpen = useCallback((event) => {
    setLangMenuAnchor(event.currentTarget);
  }, []);
  const handleCloseAllMenus = useCallback(() => {
    setAnchorElUser(null);
    setAnchorElNosotros(null);
    setLangMenuAnchor(null);
  }, []);
  const handleLanguageChange = useCallback((langCode) => {
    setLangMenuAnchor(null);
    setCurrentLang(langCode);
    localStorage.setItem('preferredLanguage', langCode);
    // Notificar a otros componentes del cambio de idioma
    window.dispatchEvent(new StorageEvent('storage', { key: 'preferredLanguage', newValue: langCode }));
  }, []);
  const handleLogout = useCallback(() => {
    // Cerrar todos los menús primero
    setAnchorElUser(null);
    setAnchorElNosotros(null);
    setLangMenuAnchor(null);
    setMobileDrawerOpen(false);
    // Pequeño delay para evitar ghosting visual
    setTimeout(() => {
      dispatch(salirSesion());
      localStorage.removeItem('user');
      navigate('/');
    }, 100);
  }, [dispatch, navigate]);
  const navigateAndClose = useCallback((path) => {
    setAnchorElUser(null);
    setAnchorElNosotros(null);
    setLangMenuAnchor(null);
    setMobileDrawerOpen(false);
    navigate(path);
  }, [navigate]);
  // Función para scroll a secciones
  const scrollToSection = useCallback((ref) => {
    setAnchorElNosotros(null);
    setMobileDrawerOpen(false);
    setMobileNosotrosOpen(false);
    setTimeout(() => {
      if (ref && ref.current) {
        ref.current.scrollIntoView({ behavior: 'smooth' });
      } else if (onScrollToSection) {
        onScrollToSection(ref);
      }
    }, 100);
  }, [onScrollToSection]);
  // Ir al inicio
  const handleGoHome = useCallback(() => {
    handleCloseAllMenus();
    setMobileDrawerOpen(false);
    if (location.pathname !== '/' && location.pathname !== '/home') {
      navigate('/');
    } else {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [handleCloseAllMenus, location.pathname, navigate]);
  // Items del submenú "Nosotros" - con key única basada en idioma
  const nosotrosItems = [
    { key: `aboutUs-${currentLang}`, label: t.nav.aboutUs, ref: aboutUsRef, icon: <Info sx={{ fontSize: 20 }} /> },
    { key: `history-${currentLang}`, label: t.nav.history, ref: historiaRef, icon: <History sx={{ fontSize: 20 }} /> },
    { key: `values-${currentLang}`, label: t.nav.values, ref: valoresRef, icon: <Favorite sx={{ fontSize: 20 }} /> },
    { key: `functions-${currentLang}`, label: t.nav.functions, ref: funcionesRef, icon: <EmojiPeople sx={{ fontSize: 20 }} /> }
  ];
  // Determinar si mostrar navbar con fondo o transparente
  const showSolidNavbar = scrolled || !isFullscreenPage;
  return (
    <>
      <AppBar 
        position="fixed"
        elevation={showSolidNavbar ? 8 : 0}
        sx={{
          background: showSolidNavbar 
            ? `linear-gradient(135deg, ${colors.black} 0%, ${colors.darkGrey} 100%)`
            : 'linear-gradient(180deg, rgba(0,0,0,0.85) 0%, rgba(0,0,0,0.4) 100%)',
          // SIN backdrop-filter para evitar ghosting
          transition: 'background 0.4s ease, box-shadow 0.4s ease',
          borderBottom: showSolidNavbar ? `2px solid ${colors.gold}50` : 'none',
          boxShadow: showSolidNavbar ? `0 4px 30px rgba(212, 175, 55, 0.2)` : 'none',
          zIndex: 1200
        }}
      >
        <Container maxWidth="xl">
          <Toolbar 
            key={`toolbar-${currentLang}`}
            disableGutters 
            sx={{ py: showSolidNavbar ? 0.5 : 1.5, justifyContent: 'space-between', minHeight: { xs: 64, md: 70 } }}
          >
            {/* Logo + Nombre */}
            <Box 
              sx={{ display: 'flex', alignItems: 'center', gap: 2, cursor: 'pointer' }}
              onClick={handleGoHome}
            >
              <Box 
                component="img" 
                src={logo} 
                alt="Logo Capellanes" 
                sx={{ 
                  height: { xs: 40, md: showSolidNavbar ? 45 : 55 }, 
                  width: 'auto', 
                  filter: 'drop-shadow(0 0 10px rgba(212, 175, 55, 0.6))', 
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    filter: 'drop-shadow(0 0 15px rgba(212, 175, 55, 0.9))',
                    transform: 'scale(1.05)'
                  }
                }} 
              />
              <Box 
                sx={{ 
                  display: { xs: 'none', md: 'block' },
                  WebkitFontSmoothing: 'antialiased'
                }}
              >
                <Typography 
                  variant="h6" 
                  sx={{ 
                    color: colors.gold, 
                    fontWeight: 800, 
                    fontSize: showSolidNavbar ? '1rem' : '1.15rem',
                    lineHeight: 1.2,
                    letterSpacing: '0.5px'
                  }}
                >
                  {t.nav.brandName}
                </Typography>
                <Typography 
                  sx={{ 
                    color: 'rgba(255,255,255,0.6)', 
                    fontSize: '0.7rem',
                    letterSpacing: '1px',
                    textTransform: 'uppercase',
                    display: showSolidNavbar ? 'none' : 'block'
                  }}
                >
                  América Latina
                </Typography>
              </Box>
            </Box>
            {/* Navegación Desktop */}
            <Box 
              sx={{ 
                display: { xs: 'none', lg: 'flex' }, 
                alignItems: 'center',
                bgcolor: showSolidNavbar ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.5)',
                borderRadius: 3,
                px: 2,
                py: 0.5,
                border: `1px solid ${showSolidNavbar ? colors.gold + '30' : 'rgba(255,255,255,0.15)'}`
              }}
            >
              {/* Inicio */}
              <Button 
                onClick={handleGoHome} 
                startIcon={<HomeIcon sx={{ fontSize: 18 }} />}
                sx={{ 
                  color: colors.white, 
                  fontWeight: 500, 
                  fontSize: '0.85rem',
                  px: 2,
                  py: 1,
                  borderRadius: 2,
                  textTransform: 'none',
                  '&:hover': { 
                    bgcolor: `${colors.gold}25`, 
                    color: colors.gold,
                  }
                }}
              >
                {t.nav.home}
              </Button>
              <Box sx={{ width: 4, height: 4, borderRadius: '50%', bgcolor: `${colors.gold}50`, mx: 0.5 }} />
              {/* Nosotros - Menú desplegable */}
              <Button 
                onClick={handleOpenNosotrosMenu}
                endIcon={anchorElNosotros ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                sx={{ 
                  color: anchorElNosotros ? colors.gold : colors.white, 
                  fontWeight: 500, 
                  fontSize: '0.85rem',
                  px: 2,
                  py: 1,
                  borderRadius: 2,
                  textTransform: 'none',
                  bgcolor: anchorElNosotros ? `${colors.gold}25` : 'transparent',
                  '&:hover': { 
                    bgcolor: `${colors.gold}25`, 
                    color: colors.gold,
                  }
                }}
              >
                {t.nav.aboutUsMenu || 'Nosotros'}
              </Button>
              <Box sx={{ width: 4, height: 4, borderRadius: '50%', bgcolor: `${colors.gold}50`, mx: 0.5 }} />
              {/* Capellanías */}
              <Button 
                onClick={() => scrollToSection(chaplainciesRef)} 
                startIcon={<Church sx={{ fontSize: 18 }} />}
                sx={{ 
                  color: colors.white, 
                  fontWeight: 500, 
                  fontSize: '0.85rem',
                  px: 2,
                  py: 1,
                  borderRadius: 2,
                  textTransform: 'none',
                  '&:hover': { 
                    bgcolor: `${colors.gold}25`, 
                    color: colors.gold,
                  }
                }}
              >
                {t.nav.chaplaincies}
              </Button>
              <Box sx={{ width: 4, height: 4, borderRadius: '50%', bgcolor: `${colors.gold}50`, mx: 0.5 }} />
              {/* Contacto */}
              <Button 
                onClick={() => scrollToSection(contactoRef)} 
                startIcon={<Phone sx={{ fontSize: 18 }} />}
                sx={{ 
                  color: colors.white, 
                  fontWeight: 500, 
                  fontSize: '0.85rem',
                  px: 2,
                  py: 1,
                  borderRadius: 2,
                  textTransform: 'none',
                  '&:hover': { 
                    bgcolor: `${colors.gold}25`, 
                    color: colors.gold,
                  }
                }}
              >
                {t.nav.contact}
              </Button>
              {/* Estructura - Solo si está logueado */}
              {isLoggedIn && (
                <>
                  <Box sx={{ width: 4, height: 4, borderRadius: '50%', bgcolor: `${colors.gold}50`, mx: 0.5 }} />
                  <Button 
                    onClick={() => navigateAndClose('/estructura')} 
                    startIcon={<AccountTree sx={{ fontSize: 18 }} />}
                    sx={{ 
                      color: colors.gold, 
                      fontWeight: 600, 
                      fontSize: '0.85rem',
                      px: 2,
                      py: 1,
                      borderRadius: 2,
                      textTransform: 'none',
                      border: `1px solid ${colors.gold}40`,
                      '&:hover': { 
                        bgcolor: `${colors.gold}25`,
                        borderColor: colors.gold
                      }
                    }}
                  >
                    {t.nav.structure}
                  </Button>
                </>
              )}
            </Box>
            {/* Acciones derecha */}
            <Box sx={{ display: 'flex', gap: { xs: 0.5, md: 1 }, alignItems: 'center' }}>
              {/* Selector de idioma */}
              <Button 
                onClick={handleLangMenuOpen} 
                sx={{ 
                  color: colors.gold, 
                  minWidth: 'auto', 
                  px: { xs: 1, md: 2 },
                  py: 1,
                  borderRadius: 2,
                  border: `1px solid ${colors.gold}40`,
                  bgcolor: 'rgba(212, 175, 55, 0.1)',
                  '&:hover': { 
                    bgcolor: `${colors.gold}25`,
                    borderColor: colors.gold
                  } 
                }} 
                endIcon={<KeyboardArrowDown />}
              >
                <Language sx={{ mr: { xs: 0, sm: 0.5 }, fontSize: 20 }} />
                <Box 
                  component="span"
                  sx={{ 
                    display: { xs: 'none', sm: 'inline-flex' }, 
                    alignItems: 'center', 
                    gap: 0.5
                  }}
                >
                  <span>{currentLanguage?.flag}</span>
                  <span style={{ fontWeight: 600 }}>{currentLanguage?.code.toUpperCase()}</span>
                </Box>
              </Button>
              {/* Menú de idiomas */}
              <Menu 
                anchorEl={langMenuAnchor} 
                open={Boolean(langMenuAnchor)} 
                onClose={() => setLangMenuAnchor(null)}
                TransitionProps={{ timeout: 200 }}
                PaperProps={{ 
                  sx: { 
                    bgcolor: colors.darkGrey, 
                    border: `2px solid ${colors.gold}50`, 
                    mt: 1,
                    borderRadius: 2,
                    minWidth: 150,
                    boxShadow: '0 8px 32px rgba(0,0,0,0.4)'
                  } 
                }}
              >
                {availableLanguages.map((lang) => (
                  <MenuItem 
                    key={`lang-${lang.code}`}
                    onClick={() => handleLanguageChange(lang.code)} 
                    selected={currentLang === lang.code} 
                    sx={{ 
                      color: colors.white, 
                      py: 1.5,
                      transition: 'all 0.2s ease',
                      '&:hover': { bgcolor: `${colors.gold}20` }, 
                      '&.Mui-selected': { 
                        bgcolor: `${colors.gold}30`, 
                        '&:hover': { bgcolor: `${colors.gold}40` } 
                      } 
                    }}
                  >
                    <ListItemIcon sx={{ fontSize: '1.5rem', minWidth: 40 }}>{lang.flag}</ListItemIcon>
                    <ListItemText primary={lang.name} />
                  </MenuItem>
                ))}
              </Menu>
              {/* Redes sociales - Solo desktop grande */}
              <Box sx={{ display: { xs: 'none', xl: 'flex' }, gap: 0.5 }}>
                {[
                  { icon: <Facebook fontSize="small" />, url: 'https://facebook.com', key: 'fb' }, 
                  { icon: <Instagram fontSize="small" />, url: 'https://instagram.com', key: 'ig' }, 
                  { icon: <WhatsApp fontSize="small" />, url: 'https://whatsapp.com', key: 'wa' }
                ].map((social) => (
                  <IconButton 
                    key={social.key}
                    size="small" 
                    onClick={() => window.open(social.url, '_blank')} 
                    sx={{ 
                      color: colors.gold, 
                      bgcolor: 'rgba(212, 175, 55, 0.1)',
                      transition: 'all 0.3s ease',
                      '&:hover': { 
                        bgcolor: colors.gold,
                        color: colors.black,
                        transform: 'scale(1.1)' 
                      }
                    }}
                  >
                    {social.icon}
                  </IconButton>
                ))}
              </Box>
              {/* Botones de autenticación */}
              {isLoggedIn ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box sx={{ display: { xs: 'none', md: 'block' }, textAlign: 'right' }}>
                    <Typography variant="body2" sx={{ color: colors.gold, fontWeight: 600, lineHeight: 1.2 }}>
                      {nombreAMostrar}
                    </Typography>
                    {esAdmin && (
                      <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.7rem' }}>
                        Admin
                      </Typography>
                    )}
                  </Box>
                  <Button 
                    onClick={handleOpenUserMenu} 
                    sx={{ 
                      p: 0, 
                      minWidth: 'auto', 
                      borderRadius: '50%',
                      '&:hover': { bgcolor: 'rgba(212, 175, 55, 0.2)' } 
                    }}
                  >
                    <Avatar 
                      sx={{ 
                        width: 42, 
                        height: 42, 
                        bgcolor: esAdmin ? '#9C27B0' : colors.gold,
                        color: esAdmin ? 'white' : colors.black,
                        fontWeight: 700,
                        border: `2px solid ${colors.gold}`,
                        boxShadow: '0 2px 8px rgba(0,0,0,0.3)'
                      }}
                      src={user?.imagen || undefined}
                    >
                      {!user?.imagen && <PersonIcon />}
                    </Avatar>
                  </Button>
                </Box>
              ) : (
                <>
                  <Button 
                    onClick={() => navigateAndClose('/login')} 
                    sx={{ 
                      color: colors.gold, 
                      fontWeight: 600, 
                      display: { xs: 'none', md: 'flex' },
                      borderRadius: 2,
                      px: 2,
                      transition: 'all 0.3s ease',
                      '&:hover': { bgcolor: 'rgba(212, 175, 55, 0.1)' } 
                    }}
                  >
                    {t.nav.login}
                  </Button>
                  <Button 
                    variant="contained"
                    onClick={() => navigateAndClose('/inscripcion')} 
                    sx={{ 
                      bgcolor: colors.gold,
                      color: colors.black,
                      py: 1, 
                      px: { xs: 2, sm: 3 }, 
                      fontSize: { xs: '0.8rem', sm: '0.9rem' },
                      fontWeight: 700,
                      borderRadius: 2,
                      display: { xs: 'none', sm: 'flex' },
                      transition: 'all 0.3s ease',
                      boxShadow: '0 4px 15px rgba(212, 175, 55, 0.4)',
                      '&:hover': {
                        bgcolor: colors.darkGold,
                        transform: 'translateY(-2px)',
                        boxShadow: '0 6px 20px rgba(212, 175, 55, 0.5)'
                      }
                    }}
                  >
                    {t.nav.join}
                  </Button>
                </>
              )}
              {/* Menú hamburguesa móvil */}
              <IconButton 
                onClick={() => setMobileDrawerOpen(true)}
                sx={{ 
                  display: { xs: 'flex', lg: 'none' },
                  color: colors.gold,
                  border: `1px solid ${colors.gold}40`,
                  ml: 1,
                  '&:hover': {
                    bgcolor: `${colors.gold}20`,
                    borderColor: colors.gold
                  }
                }}
              >
                <MenuIcon />
              </IconButton>
            </Box>
          </Toolbar>
        </Container>
      </AppBar>
      {/* Menú "Nosotros" - Desktop */}
      <Menu 
        anchorEl={anchorElNosotros} 
        open={Boolean(anchorElNosotros)} 
        onClose={() => setAnchorElNosotros(null)}
        TransitionProps={{ timeout: 200 }}
        PaperProps={{ 
          sx: { 
            bgcolor: colors.darkGrey, 
            border: `2px solid ${colors.gold}50`, 
            mt: 1,
            borderRadius: 2,
            minWidth: 220,
            boxShadow: '0 8px 32px rgba(0,0,0,0.4)',
            zIndex: 1300 // Mayor que el AppBar
          } 
        }}
      >
        {nosotrosItems.map((item) => (
          <MenuItem 
            key={item.key}
            onClick={() => scrollToSection(item.ref)} 
            sx={{ 
              color: colors.white, 
              py: 1.5,
              transition: 'all 0.2s ease',
              '&:hover': { 
                bgcolor: `${colors.gold}20`,
                color: colors.gold,
                '& .MuiListItemIcon-root': {
                  color: colors.gold
                }
              }
            }}
          >
            <ListItemIcon sx={{ color: colors.lightGold, minWidth: 36, transition: 'all 0.2s ease' }}>
              {item.icon}
            </ListItemIcon>
            <ListItemText primary={item.label} />
          </MenuItem>
        ))}
      </Menu>
      {/* Menú de usuario */}
      <Menu 
        anchorEl={anchorElUser} 
        open={Boolean(anchorElUser)} 
        onClose={() => setAnchorElUser(null)}
        TransitionProps={{ timeout: 200 }}
        PaperProps={{ 
          sx: { 
            bgcolor: colors.darkGrey, 
            border: `2px solid ${colors.gold}50`, 
            mt: 1,
            borderRadius: 2,
            minWidth: 200,
            boxShadow: '0 8px 32px rgba(0,0,0,0.4)'
          } 
        }}
      >
        <MenuItem onClick={() => navigateAndClose('/perfil')} sx={{ color: colors.white, py: 1.5, '&:hover': { bgcolor: `${colors.gold}20` } }}>
          <ListItemIcon><PersonIcon sx={{ color: colors.gold }} /></ListItemIcon>
          <ListItemText primary={t.nav.profile} />
        </MenuItem>
        {esAdmin && (
          <MenuItem onClick={() => navigateAndClose('/admin/dashboard')} sx={{ color: colors.white, py: 1.5, '&:hover': { bgcolor: `${colors.gold}20` } }}>
            <ListItemIcon><AdminIcon sx={{ color: '#9C27B0' }} /></ListItemIcon>
            <ListItemText primary="Dashboard Admin" />
          </MenuItem>
        )}
        {user?.email && (
          <MenuItem disabled sx={{ py: 1 }}>
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)' }}>{user.email}</Typography>
          </MenuItem>
        )}
        <Divider sx={{ borderColor: `${colors.gold}30`, my: 1 }} />
        <MenuItem onClick={handleLogout} sx={{ color: '#ef4444', py: 1.5, '&:hover': { bgcolor: 'rgba(239, 68, 68, 0.1)' } }}>
          <ListItemIcon><LogoutIcon sx={{ color: '#ef4444' }} /></ListItemIcon>
          <ListItemText primary={t.nav.logout} />
        </MenuItem>
      </Menu>
      {/* Drawer móvil - con key única */}
      <Drawer
        key={`drawer-${currentLang}-${isLoggedIn}`}
        anchor="right"
        open={mobileDrawerOpen}
        onClose={() => setMobileDrawerOpen(false)}
        PaperProps={{
          sx: {
            width: 300,
            bgcolor: colors.black,
            borderLeft: `2px solid ${colors.gold}`
          }
        }}
      >
        <Box sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
          {/* Header del drawer */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Box component="img" src={logo} alt="Logo" sx={{ height: 45 }} />
              <Typography sx={{ color: colors.gold, fontWeight: 700, fontSize: '0.9rem' }}>
                {t.nav.brandName}
              </Typography>
            </Box>
            <IconButton onClick={() => setMobileDrawerOpen(false)} sx={{ color: colors.gold }}>
              <Close />
            </IconButton>
          </Box>
          {/* Usuario logueado - info */}
          {isLoggedIn && (
            <Box sx={{ mb: 3, p: 2, bgcolor: `${colors.gold}10`, borderRadius: 2, border: `1px solid ${colors.gold}30` }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Avatar sx={{ bgcolor: colors.gold, color: colors.black, width: 40, height: 40 }}>
                  <PersonIcon />
                </Avatar>
                <Box>
                  <Typography sx={{ color: colors.gold, fontWeight: 600, fontSize: '0.9rem' }}>
                    {nombreAMostrar}
                  </Typography>
                  {esAdmin && (
                    <Typography sx={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.75rem' }}>
                      Administrador
                    </Typography>
                  )}
                </Box>
              </Box>
            </Box>
          )}
          {/* Lista de navegación */}
          <List sx={{ flex: 1 }}>
            {/* Inicio */}
            <ListItem disablePadding>
              <ListItemButton 
                onClick={handleGoHome}
                sx={{ 
                  py: 2,
                  borderRadius: 2,
                  mb: 1,
                  '&:hover': { bgcolor: `${colors.gold}20` }
                }}
              >
                <ListItemIcon sx={{ color: colors.gold, minWidth: 40 }}>
                  <HomeIcon />
                </ListItemIcon>
                <ListItemText 
                  primary={t.nav.home}
                  sx={{ '& .MuiTypography-root': { color: colors.lightGold, fontWeight: 500 } }} 
                />
              </ListItemButton>
            </ListItem>
            {/* Nosotros - Expandible */}
            <ListItem disablePadding>
              <ListItemButton 
                onClick={() => setMobileNosotrosOpen(!mobileNosotrosOpen)}
                sx={{ 
                  py: 2,
                  borderRadius: 2,
                  mb: 1,
                  bgcolor: mobileNosotrosOpen ? `${colors.gold}10` : 'transparent',
                  '&:hover': { bgcolor: `${colors.gold}20` }
                }}
              >
                <ListItemIcon sx={{ color: colors.gold, minWidth: 40 }}>
                  <Group />
                </ListItemIcon>
                <ListItemText 
                  primary={t.nav.aboutUsMenu || 'Nosotros'}
                  sx={{ '& .MuiTypography-root': { color: colors.lightGold, fontWeight: 500 } }} 
                />
                {mobileNosotrosOpen ? <ExpandLess sx={{ color: colors.gold }} /> : <ExpandMore sx={{ color: colors.gold }} />}
              </ListItemButton>
            </ListItem>
            <Collapse in={mobileNosotrosOpen} timeout="auto" unmountOnExit>
              <List component="div" disablePadding>
                {nosotrosItems.map((item) => (
                  <ListItemButton 
                    key={`mobile-${item.key}`}
                    onClick={() => scrollToSection(item.ref)}
                    sx={{ 
                      py: 1.5,
                      pl: 6,
                      borderRadius: 2,
                      mb: 0.5,
                      '&:hover': { bgcolor: `${colors.gold}15` }
                    }}
                  >
                    <ListItemIcon sx={{ color: colors.lightGold, minWidth: 36 }}>
                      {item.icon}
                    </ListItemIcon>
                    <ListItemText 
                      primary={item.label}
                      sx={{ '& .MuiTypography-root': { color: 'rgba(255,255,255,0.8)', fontSize: '0.9rem' } }} 
                    />
                  </ListItemButton>
                ))}
              </List>
            </Collapse>
            {/* Capellanías */}
            <ListItem disablePadding>
              <ListItemButton 
                onClick={() => scrollToSection(chaplainciesRef)}
                sx={{ 
                  py: 2,
                  borderRadius: 2,
                  mb: 1,
                  '&:hover': { bgcolor: `${colors.gold}20` }
                }}
              >
                <ListItemIcon sx={{ color: colors.gold, minWidth: 40 }}>
                  <Church />
                </ListItemIcon>
                <ListItemText 
                  primary={t.nav.chaplaincies}
                  sx={{ '& .MuiTypography-root': { color: colors.lightGold, fontWeight: 500 } }} 
                />
              </ListItemButton>
            </ListItem>
            {/* Contacto */}
            <ListItem disablePadding>
              <ListItemButton 
                onClick={() => scrollToSection(contactoRef)}
                sx={{ 
                  py: 2,
                  borderRadius: 2,
                  mb: 1,
                  '&:hover': { bgcolor: `${colors.gold}20` }
                }}
              >
                <ListItemIcon sx={{ color: colors.gold, minWidth: 40 }}>
                  <Phone />
                </ListItemIcon>
                <ListItemText 
                  primary={t.nav.contact}
                  sx={{ '& .MuiTypography-root': { color: colors.lightGold, fontWeight: 500 } }} 
                />
              </ListItemButton>
            </ListItem>
            {/* Estructura - Solo si está logueado */}
            {isLoggedIn && (
              <ListItem disablePadding>
                <ListItemButton 
                  onClick={() => navigateAndClose('/estructura')}
                  sx={{ 
                    py: 2,
                    borderRadius: 2,
                    mb: 1,
                    bgcolor: `${colors.gold}10`,
                    border: `1px solid ${colors.gold}30`,
                    '&:hover': { bgcolor: `${colors.gold}20` }
                  }}
                >
                  <ListItemIcon sx={{ color: colors.gold, minWidth: 40 }}>
                    <AccountTree />
                  </ListItemIcon>
                  <ListItemText 
                    primary={t.nav.structure}
                    sx={{ '& .MuiTypography-root': { color: colors.gold, fontWeight: 600 } }} 
                  />
                </ListItemButton>
              </ListItem>
            )}
          </List>
          <Divider sx={{ borderColor: `${colors.gold}30`, my: 2 }} />
          {/* Botones de autenticación en móvil */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {isLoggedIn ? (
              <>
                <Button
                  fullWidth
                  variant="outlined"
                  startIcon={<PersonIcon />}
                  onClick={() => navigateAndClose('/perfil')}
                  sx={{ 
                    color: colors.gold, 
                    borderColor: colors.gold,
                    py: 1.5,
                    '&:hover': { bgcolor: `${colors.gold}20`, borderColor: colors.gold }
                  }}
                >
                  {t.nav.profile}
                </Button>
                <Button
                  fullWidth
                  variant="contained"
                  startIcon={<LogoutIcon />}
                  onClick={handleLogout}
                  sx={{ 
                    bgcolor: '#ef4444',
                    color: 'white',
                    py: 1.5,
                    '&:hover': { bgcolor: '#dc2626' }
                  }}
                >
                  {t.nav.logout}
                </Button>
              </>
            ) : (
              <>
                <Button
                  fullWidth
                  variant="outlined"
                  startIcon={<LoginIcon />}
                  onClick={() => navigateAndClose('/login')}
                  sx={{ 
                    color: colors.gold, 
                    borderColor: colors.gold,
                    py: 1.5,
                    '&:hover': { bgcolor: `${colors.gold}20`, borderColor: colors.gold }
                  }}
                >
                  {t.nav.login}
                </Button>
                <Button 
                  fullWidth
                  variant="contained"
                  startIcon={<RegisterIcon />}
                  onClick={() => navigateAndClose('/inscripcion')}
                  sx={{
                    bgcolor: colors.gold,
                    color: colors.black,
                    py: 1.5,
                    fontWeight: 700,
                    '&:hover': { bgcolor: colors.darkGold }
                  }}
                >
                  {t.nav.join}
                </Button>
              </>
            )}
          </Box>
          {/* Redes sociales en móvil */}
          <Box sx={{ mt: 4, textAlign: 'center' }}>
            <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.8rem', mb: 2 }}>
              {t.footer.followTitle}
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1.5 }}>
              {[
                { icon: <Facebook />, url: 'https://facebook.com', key: 'fb-m' }, 
                { icon: <Instagram />, url: 'https://instagram.com', key: 'ig-m' }, 
                { icon: <WhatsApp />, url: 'https://whatsapp.com', key: 'wa-m' }
              ].map((social) => (
                <IconButton 
                  key={social.key}
                  onClick={() => window.open(social.url, '_blank')} 
                  sx={{ 
                    color: colors.gold, 
                    bgcolor: 'rgba(212, 175, 55, 0.1)',
                    transition: 'all 0.3s ease',
                    '&:hover': { 
                      bgcolor: colors.gold,
                      color: colors.black
                    }
                  }}
                >
                  {social.icon}
                </IconButton>
              ))}
            </Box>
          </Box>
        </Box>
      </Drawer>
    </>
  );
};
export default NavigationBarCapellanes;